import type { SelectOption } from './select-field';
import { ScrollArea } from './scroll-area';

export interface PageTabsProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  /** 탭 목록 a11y 라벨. */
  ariaLabel?: string;
}

/**
 * 페이지 탭 네비게이션(b 영역) — 타이틀 줄(a) 아래 **진짜 탭**. 사용처 = 인버터 상세분석(구성/계측/MPPT/스트링)·
 * 설정(내정보/환경설정).
 *
 * **밑줄(line) variant** — 활성 탭 하단 primary 언더라인(세그먼트 pill 아님). 탭은 tablist 시맨틱이라 native `<button
 * role="tab">` 사용(heritage Button 의 action 스타일이 언더라인 탭과 충돌).
 *
 * **정렬은 폭에 따라 갈린다** — non-pc(< lg)는 **full-width 균등 채움**(`flex-1`, 좁은 화면에서 탭 하나하나가
 * 엄지 타겟이 되도록) / pc(≥ lg)는 **내용 폭 + 좌측 정렬**(`lg:flex-none`) — 넓은 화면에서 2~4개 탭을 전 폭에
 * 늘리면 라벨 사이가 휑하게 벌어져 한 덩어리로 안 읽힌다. 언더라인 baseline 은 두 경우 모두 전 폭 스팬.
 *
 * 텍스트는 **절대 생략 금지**(`min-width:max-content`·`whitespace-nowrap`). 폭이 모자라면 공용 `ScrollArea` 로
 * **가로 스크롤**(항목 소실·생략 방지, non-pc 스와이프). `option.disabled` = 접근 불가 탭(capabilities 미해결/미지원)
 * — 흐리게+클릭 차단.
 */
export function PageTabs({ value, onValueChange, options, ariaLabel }: PageTabsProps) {
  // overscrollContain={false} — 가로만 스크롤하는 탭 스트립. base-ui Viewport 는 세로 내용이 안 넘쳐도
  // **양축 스크롤 컨테이너**라 contain 이면 탭 바 위 세로 휠을 삼켜 페이지가 안 굴러간다(가로는 그대로).
  return (
    <ScrollArea className="w-full" overscrollContain={false}>
      {/* min-w-full = 탭이 적어도 언더라인 baseline 이 전 폭 스팬. flex-1+min-content = 채우되 내용 미만 축소(생략) 금지. */}
      {/* pc 는 자식이 flex-none 이라 자연히 좌측부터 쌓인다(justify 지정 불필요). */}
      <div role="tablist" aria-label={ariaLabel} className="flex min-w-full border-b border-border">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={option.disabled}
              onClick={() => onValueChange(option.value)}
              className={`-mb-px flex-1 whitespace-nowrap border-b-2 px-4 py-2.5 text-center text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-40 lg:flex-none [min-width:max-content] ${
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </ScrollArea>
  );
}
